import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { PageResponse } from '@models/api';
import { getSales, type GetSalesResponse } from '@apis/summary';
import { getKstTodayString, getTimestampRangeToDateKst, dateToTimestamp } from '@utils/time';

const DEFAULT_STALE_TIME_MS = 60_000; // 1 minute is a good default for dashboard KPIs.

function createDashboardSalesSummaryQueryOptions(
  queryKey: string[],
  periodUnit: 'DAYS' | 'WEEKS' | 'MONTHS' | 'YEARS',
  range: () => { from: number; to: number }
): UseQueryOptions<PageResponse<GetSalesResponse>, Error> {
  return {
    queryKey,
    queryFn: () => {
      const { from, to } = range();
      return getSales({ periodUnit, from, to });
    },
    staleTime: DEFAULT_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  };
}

/**
 * Sales KPI for today (KST).
 * Uses the full KST day window: 00:00:00 ~ 23:59:59.
 */
export const useSummaryToday = () => {
  return useQuery(
    createDashboardSalesSummaryQueryOptions(['dashboardSummaryToday'], 'DAYS', () => {
      const todayKst = getKstTodayString();
      return {
        from: dateToTimestamp(todayKst, false, true, true),
        to: dateToTimestamp(todayKst, true, true, false),
      };
    })
  );
};

/**
 * Sales KPI for Week-to-date (KST).
 * Uses the KST week start (Sunday) through today's end-of-day (KST).
 */
export const useSummaryThisWeek = () => {
  return useQuery(
    createDashboardSalesSummaryQueryOptions(['dashboardSummaryThisWeek'], 'WEEKS', () =>
      getTimestampRangeToDateKst('WEEKS')
    )
  );
};

/**
 * Sales KPI for Month-to-date (KST).
 * Uses the KST month start through today's end-of-day (KST).
 */
export const useSummayThisMonth = () => {
  return useQuery(
    createDashboardSalesSummaryQueryOptions(['dashboardSummaryThisMonth'], 'MONTHS', () =>
      getTimestampRangeToDateKst('MONTHS')
    )
  );
};

/**
 * Sales KPI for Year-to-date (KST).
 * Uses the KST year start through today's end-of-day (KST).
 */
export const useSummaryThisYear = () => {
  return useQuery(
    createDashboardSalesSummaryQueryOptions(['dashboardSummaryThisYear'], 'YEARS', () =>
      getTimestampRangeToDateKst('YEARS')
    )
  );
};
